import type { ChangeEvent, FC } from "react"
import { useMemo, useState } from "react"
import { Box, Button, Flex, Select, Stack, Text } from "@chakra-ui/react"

import type {
  AccountId,
  CreateProposalGovernanceSelectorQuery,
  Governor,
} from "query/graphql"
import { useCreateProposalGovernanceSelectorQuery } from "query/graphql"
import { useRouter } from "common/hooks/useRouter"
import { ROUTES } from "common/constants/routes"
import { getWeightLabel, stringToBigNumber } from "common/helpers/bignumber"
import type { ArrayElement } from "common/types/array"
import { useCorrectChain } from "web3/hooks/useCorrectChain"
import {
  byGovernancePriority,
  getParameterDatesLabel,
  isMainGovernor,
} from "governance/helpers/governance"
import { useChainReference } from "web3/hooks/useChainReference"
import {
  StepName,
  type CreateProposalFormState,
} from "createProposal/types/form"
import CardContainer from "common/components/CardContainer"

type Chains = CreateProposalGovernanceSelectorQuery["chains"]
type Chain = ArrayElement<Chains>

type Props = {
  slug: string
  targetGovernanceId?: AccountId
  isEditMode?: boolean
  organizationId: string
  state: Pick<CreateProposalFormState, "currentStep">
}
const CreateProposalGovernanceSelector: FC<Props> = ({
  slug,
  isEditMode,
  organizationId,
  targetGovernanceId = null,
  state,
}) => {
  const { data } = useCreateProposalGovernanceSelectorQuery({
    input: {
      filters: {
        organizationId,
      },
    },
  })

  const { currentStep } = state
  const isActive = currentStep === StepName.SELECTOR_GOVERNOR

  const { push } = useRouter()
  const [activeTargetGovernanceId, setActiveTargetGovernanceId] =
    useState<AccountId>(targetGovernanceId || "")

  const governances = useMemo(() => {
    if (!data) {
      return []
    }
    const governances = (data.governors?.nodes as Governor[])
      ?.filter(isMainGovernor)
      .sort(byGovernancePriority)

    return governances
  }, [data])

  const handleChangeTargetGovernanceId = (
    e: ChangeEvent<HTMLSelectElement>,
  ) => {
    setActiveTargetGovernanceId(e.target.value)
  }

  const activeTargetGovernance = useMemo(() => {
    if (governances && governances.length > 0) {
      const governance = governances.find(
        (governance) => governance.id === activeTargetGovernanceId,
      )

      return governance
    }

    return undefined
  }, [activeTargetGovernanceId, governances])

  const handleClickContinue = () => {
    if (
      targetGovernanceId?.toLowerCase() !==
      activeTargetGovernanceId.toLowerCase()
    ) {
      push(ROUTES.governance.proposal.create(slug, activeTargetGovernanceId))
    }
  }

  if (!isActive) return null

  const chains = data?.chains

  const isDisabled = governances.length === 0 || isEditMode

  const isSwitchGovernorDisabled =
    targetGovernanceId?.toLowerCase() === activeTargetGovernanceId.toLowerCase()

  return (
    <CardContainer mt={0} p={0} pt={{ base: 6, sm: 2 }} width="100%">
      <Stack py={2} spacing={6}>
        <Select
          defaultValue={activeTargetGovernanceId}
          isDisabled={isDisabled}
          value={activeTargetGovernanceId}
          onChange={handleChangeTargetGovernanceId}
        >
          {!targetGovernanceId ? (
            <option value="">Choose Governor</option>
          ) : null}
          {governances.map((governance) => (
            <option key={`gov-${governance.id}`} value={governance.id}>
              {governance.name}
            </option>
          ))}
        </Select>
        {activeTargetGovernance && chains ? (
          <GovernanceInformation
            chains={chains}
            governor={activeTargetGovernance}
          />
        ) : null}

        {isEditMode ? null : (
          <Box>
            <Button
              isDisabled={isSwitchGovernorDisabled}
              variant="primary"
              width={{ base: "100%", md: "auto" }}
              onClick={handleClickContinue}
            >
              Change target Governor
            </Button>
          </Box>
        )}
      </Stack>
    </CardContainer>
  )
}

type GovernanceInformationProps = {
  governor: Governor
  chains: Chains
}
const GovernanceInformation: FC<GovernanceInformationProps> = ({
  governor,
  chains,
}) => {
  const chainReference = useChainReference()
  const { parameters, quorum, token, chainId } = governor
  const { proposalThreshold } = parameters
  const { decimals } = token

  const chain = chains
    ? chains.find((chain: Chain) => chain && chain.id === chainId)
    : null

  const getChain = () => {
    if (chain?.useLayer1VotingPeriod && chains) {
      const layer1Chain = chains.find(
        (element: Chain) => element?.id === chain?.layer1Id,
      )

      if (layer1Chain) {
        return { ...chain, blockTime: layer1Chain.blockTime }
      }
    } else {
      return chain
    }
  }

  const fixedChain = getChain()

  const { isCorrectChain } = useCorrectChain({ chainId, chainReference })

  const { votingPeriodLabel, votingDelayLabel } = getParameterDatesLabel(
    parameters,
    fixedChain,
  )

  const bigProposalThreshold = proposalThreshold
    ? stringToBigNumber(proposalThreshold)
    : ""
  const proposalThresholdLabel =
    bigProposalThreshold && typeof decimals !== "undefined" && decimals >= 0
      ? getWeightLabel(bigProposalThreshold, decimals)
      : ""

  const bigQuorum = stringToBigNumber(quorum)
  const quorumLabel = getWeightLabel(bigQuorum, decimals)

  return (
    <Stack
      bg="gray.50"
      h="full"
      position="relative"
      px={6}
      py={isCorrectChain ? 4 : 2}
      spacing={4}
      w="full"
    >
      <Flex align="center" justify="space-between" w="full">
        <Text color="gray.600" textStyle="body.bold.md">
          Proposal threshold
        </Text>
        <Text color="gray.600" textStyle="body.regular.md">
          {proposalThresholdLabel}
        </Text>
      </Flex>
      <Flex align="center" justify="space-between" w="full">
        <Text color="gray.600" textStyle="body.bold.md">
          Quorum needed
        </Text>
        <Text color="gray.600" textStyle="body.regular.md">
          {quorumLabel}
        </Text>
      </Flex>
      <Flex align="center" justify="space-between" w="full">
        <Text color="gray.600" textStyle="body.bold.md">
          Proposal delay
        </Text>
        <Text color="gray.600" textStyle="body.regular.md">
          {votingDelayLabel}
        </Text>
      </Flex>
      <Flex align="center" justify="space-between" w="full">
        <Text color="gray.600" textStyle="body.bold.md">
          Voting period
        </Text>
        <Text color="gray.600" textStyle="body.regular.md">
          {votingPeriodLabel}
        </Text>
      </Flex>
    </Stack>
  )
}

export default CreateProposalGovernanceSelector
